import React, { useEffect, useState } from "react";
import { Input } from "../../design/input";
import { Select } from "../../design/select";
import { Checkbox, CheckboxField } from "../../design/checkbox";
import { Field, FieldGroup, Fieldset, Label } from "../../design/fieldset";
import SelectArtist from "../../components/SelectArtist";
import axios from "axios";
import AuctionRecordListItem from "./components/AuctionRecordListItem";
import { Button } from "../../design/button";
import { Textarea } from "../../design/textarea";
import { MarketAnalytics } from "../../models/AuctionRecord";

const categories = ["painting", "sculpture", "photograph", "drawing"];

const sortOptions = [
  { label: "Sale Date (Newest)", value: "sale_date_desc" },
  { label: "Hammer Price (High to Low)", value: "hammer_price_desc" },
  { label: "Hammer Price (Low to High)", value: "hammer_price_asc" },
];

interface Filter {
  artist?: any;
  category?: string;
  isEdition?: boolean;
  auctionPerformance?: string;
  minHammerPrice?: number;
  maxHammerPrice?: number;
  maxWidth?: number;
  maxHeight?: number;
  minWidth?: number;
  minHeight?: number;
  description?: string;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toLocaleString()}`;
};

const AuctionRecordsPageV2: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<MarketAnalytics | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [filters, setFilters] = useState<Filter>({ isEdition: false });
  const [sortOption, setSortOption] = useState<string>(sortOptions[1].value);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    handleSubmit();
  }, [sortOption]);

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/price_database`,
        {
          params: {
            artist: filters?.artist?.objectID,
            category: filters?.category,
            is_edition: filters.isEdition,
            lot_performance: filters.auctionPerformance,
            min_hammer_price: filters.minHammerPrice,
            max_hammer_price: filters.maxHammerPrice,
            min_width: filters.minWidth,
            max_width: filters.maxWidth,
            min_height: filters.minHeight,
            max_height: filters.maxHeight,
            description: filters.description,
            sort_option: sortOption,
            include_analytics: filters.artist ? "true" : "false",
          },
        }
      );
      setResults(response.data.data);
      setAnalytics(response.data.analytics);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching auction records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRiskBadge = (risk: string) => {
    const styles: Record<string, string> = {
      low: "bg-emerald-50 text-emerald-700 border-emerald-200",
      moderate_low: "bg-blue-50 text-blue-700 border-blue-200",
      moderate: "bg-amber-50 text-amber-700 border-amber-200",
      high: "bg-orange-50 text-orange-700 border-orange-200",
      very_high: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return styles[risk] || "bg-gallery-100 text-gallery-600 border-gallery-200";
  };

  const getGradeBadge = (grade: number) => {
    if (grade >= 75) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (grade >= 60) return "bg-blue-50 text-blue-700 border-blue-200";
    if (grade >= 40) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
  };

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-[1600px] mx-auto px-6 py-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
                Market Intelligence
              </p>
              <h1 className="font-display text-display-sm text-gallery-900 mb-2">
                Price Database
              </h1>
              <p className="text-gallery-500">
                Search millions of auction records with AI-powered analytics
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gallery-600 bg-gallery-100 hover:bg-gallery-200 rounded-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-72 shrink-0">
              <div className="sticky top-6 bg-white border border-gallery-200 rounded-sm">
                <div className="px-5 py-4 border-b border-gallery-100">
                  <h2 className="font-display text-sm text-gallery-900">Search Filters</h2>
                </div>
                <div className="p-5">
                  <Fieldset>
                    <FieldGroup>
                      <Field>
                        <Label className="text-sm font-medium text-gallery-700 mb-1.5 block">Artist</Label>
                        <SelectArtist
                          artist={filters.artist}
                          onSelectArtist={(artist: any) => setFilters({ ...filters, artist })}
                        />
                      </Field>

                      <Field>
                        <Label className="text-sm font-medium text-gallery-700 mb-1.5 block">Category</Label>
                        <Select
                          value={filters.category}
                          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        >
                          <option value="">All Categories</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                          ))}
                        </Select>
                      </Field>

                      <Field>
                        <Label className="text-sm font-medium text-gallery-700 mb-1.5 block">Price Range (USD)</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Min"
                            value={filters.minHammerPrice || ""}
                            type="number"
                            onChange={(e) => setFilters({ ...filters, minHammerPrice: parseFloat(e.target.value) || undefined })}
                          />
                          <Input
                            placeholder="Max"
                            value={filters.maxHammerPrice || ""}
                            type="number"
                            onChange={(e) => setFilters({ ...filters, maxHammerPrice: parseFloat(e.target.value) || undefined })}
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label className="text-sm font-medium text-gallery-700 mb-1.5 block">Dimensions (cm)</Label>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Min W"
                              value={filters.minWidth || ""}
                              type="number"
                              onChange={(e) => setFilters({ ...filters, minWidth: parseFloat(e.target.value) || undefined })}
                            />
                            <Input
                              placeholder="Max W"
                              value={filters.maxWidth || ""}
                              type="number"
                              onChange={(e) => setFilters({ ...filters, maxWidth: parseFloat(e.target.value) || undefined })}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Min H"
                              value={filters.minHeight || ""}
                              type="number"
                              onChange={(e) => setFilters({ ...filters, minHeight: parseFloat(e.target.value) || undefined })}
                            />
                            <Input
                              placeholder="Max H"
                              value={filters.maxHeight || ""}
                              type="number"
                              onChange={(e) => setFilters({ ...filters, maxHeight: parseFloat(e.target.value) || undefined })}
                            />
                          </div>
                        </div>
                      </Field>

                      <CheckboxField>
                        <Checkbox
                          checked={filters.isEdition}
                          onChange={(checked) => setFilters({ ...filters, isEdition: !!checked })}
                        />
                        <Label className="text-sm text-gallery-700">Limited Edition Only</Label>
                      </CheckboxField>

                      <Field>
                        <Label className="text-sm font-medium text-gallery-700 mb-1.5 block">
                          AI Search
                          <span className="ml-1.5 text-xs text-gallery-400">(Beta)</span>
                        </Label>
                        <Textarea
                          value={filters.description || ""}
                          onChange={(e) => setFilters({ ...filters, description: e.target.value })}
                          placeholder="Describe the artwork..."
                          rows={2}
                        />
                      </Field>

                      <Button onClick={handleSubmit} className="w-full" disabled={isLoading}>
                        {isLoading ? "Searching..." : "Search Database"}
                      </Button>
                    </FieldGroup>
                  </Fieldset>
                </div>
              </div>
            </div>
          )}

          {/* Results Area */}
          <div className="flex-1 min-w-0">
            {/* Controls Bar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gallery-500">
                {stats ? `${stats.count.toLocaleString()} results` : "Ready to search"}
              </p>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-52"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* Market Analytics Panel */}
            {analytics && filters.artist && (
              <div className="bg-white border border-gallery-200 rounded-sm mb-6">
                <div className="px-6 py-4 border-b border-gallery-100 flex items-center justify-between">
                  <div>
                    <h2 className="font-display text-lg text-gallery-900">Market Analytics</h2>
                    <p className="text-sm text-gallery-500">{filters.artist.name}</p>
                  </div>
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-sm border ${getGradeBadge(analytics.summary?.investmentGrade || 0)}`}>
                    Grade: {analytics.summary?.investmentGrade || 0}/100
                  </span>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    <div>
                      <p className="stat-label">CAGR</p>
                      <p className={`stat-value text-xl ${(analytics.performance?.cagr || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {(analytics.performance?.cagr || 0) >= 0 ? '+' : ''}{analytics.performance?.cagr?.toFixed(1) || 0}%
                      </p>
                      <p className="text-xs text-gallery-400 mt-0.5">{analytics.summary?.yearsOfData || 0} years</p>
                    </div>
                    <div>
                      <p className="stat-label">Volatility</p>
                      <p className="stat-value text-xl">{analytics.risk?.volatility || 0}%</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-sm border ${getRiskBadge(analytics.risk?.riskLevel || 'moderate')}`}>
                        {(analytics.risk?.riskLevel || 'N/A').replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div>
                      <p className="stat-label">Liquidity Score</p>
                      <p className="stat-value text-xl">{analytics.market?.liquidityScore || 0}/100</p>
                      <p className="text-xs text-gallery-400 mt-0.5">{analytics.market?.avgLotsPerYear?.toFixed(1) || 0} lots/yr</p>
                    </div>
                    <div>
                      <p className="stat-label">Sharpe Ratio</p>
                      <p className="stat-value text-xl">{analytics.risk?.sharpeRatio?.toFixed(2) || 'N/A'}</p>
                      <p className="text-xs text-gallery-400 mt-0.5">Risk-adjusted</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gallery-100">
                    <div className="text-center">
                      <p className="text-xs text-gallery-400 mb-1">Avg Annual Return</p>
                      <p className="font-medium text-gallery-900">{analytics.performance?.avgAnnualReturn?.toFixed(1) || 0}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gallery-400 mb-1">Trend</p>
                      <p className="font-medium text-gallery-900 capitalize">{analytics.trend?.direction || 'N/A'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gallery-400 mb-1">Avg Premium</p>
                      <p className="font-medium text-gallery-900">
                        {(analytics.pricing?.avgPremium || 0) > 0 ? '+' : ''}{analytics.pricing?.avgPremium || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Summary */}
            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-gallery-200 rounded-sm p-4 text-center">
                  <p className="stat-label">Results</p>
                  <p className="stat-value text-lg">{stats.count.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-gallery-200 rounded-sm p-4 text-center">
                  <p className="stat-label">Total Value</p>
                  <p className="stat-value text-lg">{formatCurrency(stats.totalValue / 100)}</p>
                </div>
                <div className="bg-white border border-gallery-200 rounded-sm p-4 text-center">
                  <p className="stat-label">Average</p>
                  <p className="stat-value text-lg">{formatCurrency(stats.avgPrice)}</p>
                </div>
                <div className="bg-white border border-gallery-200 rounded-sm p-4 text-center">
                  <p className="stat-label">Median</p>
                  <p className="stat-value text-lg">{formatCurrency(stats.medianPrice)}</p>
                </div>
              </div>
            )}

            {/* Results List */}
            <div className="space-y-4">
              {results.map((item) => (
                <AuctionRecordListItem key={item._id} item={item} />
              ))}

              {results.length === 0 && !isLoading && (
                <div className="text-center py-16 bg-white border border-gallery-200 rounded-sm">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gallery-100 mb-4">
                    <svg className="w-8 h-8 text-gallery-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="font-display text-lg text-gallery-700 mb-2">No Results Found</h3>
                  <p className="text-gallery-500">Try adjusting your filters or search criteria</p>
                </div>
              )}

              {isLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full border-2 border-gallery-200"></div>
                    <div className="absolute inset-0 w-10 h-10 rounded-full border-2 border-transparent border-t-gallery-800 animate-spin"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionRecordsPageV2;
